// =====================================================
// Swiss Finance OS - Analytics Synchronization Hooks
// React hooks for real-time dashboard analytics
// =====================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  analyticsAggregationService,
  DashboardSummary,
  TransactionAnalytics,
  ProcessingAnalytics,
  TeamAnalytics,
  MandateAnalytics
} from '@/services/analytics-aggregation';
import { useRealtimeContext } from '@/components/realtime/realtime-context';
import { useToast } from '@/hooks/use-toast';

interface UseAnalyticsSyncOptions {
  refreshInterval?: number;
  enabled?: boolean;
  mandateId?: string;
  onError?: (error: Error) => void;
  showErrorToasts?: boolean;
}

/**
 * Hook for dashboard summary with real-time sync
 */
export function useDashboardSummary(options: UseAnalyticsSyncOptions = {}) {
  const { refreshInterval = 60000, enabled = true, onError, showErrorToasts = true } = options;
  const { latestUpdate } = useRealtimeContext();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => analyticsAggregationService.getDashboardSummary(),
    refetchInterval: refreshInterval,
    enabled,
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  // Handle errors
  useEffect(() => {
    if (query.error && onError) {
      onError(query.error);
    }

    if (query.error && showErrorToasts) {
      toast({
        title: 'Dashboard Data Error',
        description: 'Unable to load dashboard summary. Data may be outdated.',
        variant: 'destructive',
        duration: 5000,
      });
    }
  }, [query.error, onError, showErrorToasts, toast]);
  useEffect(() => {
    if (latestUpdate?.type === 'transaction_processed') {
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    }
  }, [latestUpdate, queryClient]);

  return {
    ...query,
    refresh: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    }, [queryClient]),
    hasError: !!query.error,
    errorMessage: query.error?.message,
  };
}

/**
 * Hook for transaction analytics with real-time updates
 */
export function useTransactionAnalytics(options: UseAnalyticsSyncOptions = {}) {
  const { refreshInterval = 30000, enabled = true, mandateId } = options;
  const { latestUpdate } = useRealtimeContext();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['transaction-analytics', mandateId],
    queryFn: () => analyticsAggregationService.getTransactionAnalytics(mandateId),
    refetchInterval: refreshInterval,
    enabled,
    staleTime: 15000,
  });

  // Real-time updates
  useEffect(() => {
    if (latestUpdate?.type === 'transaction_processed') {
      queryClient.invalidateQueries({ queryKey: ['transaction-analytics'] });
    }
  }, [latestUpdate, queryClient]);

  return {
    ...query,
    refresh: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['transaction-analytics', mandateId] });
    }, [queryClient, mandateId]),
  };
}

/**
 * Hook for mandate-specific analytics with historical data
 */
export function useMandateAnalytics(
  mandateId: string,
  periodType: 'daily' | 'monthly' = 'daily',
  limit: number = 30,
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;
  const { latestUpdate } = useRealtimeContext();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['mandate-analytics', mandateId, periodType, limit],
    queryFn: () => analyticsAggregationService.getMandateAnalytics(mandateId, periodType, limit),
    enabled: enabled && !!mandateId,
    staleTime: 60000, // Historical data is less frequently updated
  });

  // Update when transactions for this mandate change
  useEffect(() => {
    if (latestUpdate?.type === 'transaction_processed' &&
        latestUpdate.data?.record?.mandate_id === mandateId) {
      queryClient.invalidateQueries({ queryKey: ['mandate-analytics', mandateId] });
    }
  }, [latestUpdate, queryClient, mandateId]);

  const refreshMandateAnalytics = useCallback(async () => {
    await analyticsAggregationService.refreshMandateAnalytics(mandateId);
    queryClient.invalidateQueries({ queryKey: ['mandate-analytics', mandateId] });
  }, [mandateId, queryClient]);

  return {
    ...query,
    refresh: refreshMandateAnalytics,
  };
}

/**
 * Hook for processing queue analytics
 */
export function useProcessingAnalytics(options: UseAnalyticsSyncOptions = {}) {
  const { refreshInterval = 15000, enabled = true } = options;
  const { latestUpdate } = useRealtimeContext();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['processing-analytics'],
    queryFn: () => analyticsAggregationService.getProcessingAnalytics(),
    refetchInterval: refreshInterval,
    enabled,
    staleTime: 10000, // Processing data changes frequently
  });

  // Update on processing status changes
  useEffect(() => {
    if (latestUpdate?.type === 'processing_status' || latestUpdate?.type === 'file_upload_status') {
      queryClient.invalidateQueries({ queryKey: ['processing-analytics'] });
    }
  }, [latestUpdate, queryClient]);

  return {
    ...query,
    refresh: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['processing-analytics'] });
    }, [queryClient]),
  };
}

/**
 * Hook for team productivity analytics
 */
export function useTeamAnalytics(userId?: string, days: number = 30, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { latestUpdate } = useRealtimeContext();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['team-analytics', userId, days],
    queryFn: () => analyticsAggregationService.getTeamAnalytics(userId, days),
    enabled,
    staleTime: 300000, // Team analytics update less frequently (5 minutes)
  });

  // Update on team activity
  useEffect(() => {
    if (latestUpdate?.type === 'team_activity' &&
        (!userId || latestUpdate.data?.userId === userId)) {
      queryClient.invalidateQueries({ queryKey: ['team-analytics'] });
    }
  }, [latestUpdate, queryClient, userId]);

  return {
    ...query,
    refresh: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['team-analytics', userId, days] });
    }, [queryClient, userId, days]),
  };
}

/**
 * Hook for optimized dashboard summary view (materialized view)
 */
export function useDashboardSummaryView(options: UseAnalyticsSyncOptions = {}) {
  const { refreshInterval = 300000, enabled = true } = options; // 5 minutes for materialized view
  const { latestUpdate } = useRealtimeContext();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['dashboard-summary-view'],
    queryFn: () => analyticsAggregationService.getDashboardSummaryView(),
    refetchInterval: refreshInterval,
    enabled,
    staleTime: 120000, // 2 minutes stale time for materialized view
  });

  // Refresh materialized view on significant updates
  const refreshCount = useRef(0);
  useEffect(() => {
    if (latestUpdate?.type === 'transaction_processed') {
      refreshCount.current += 1;

      // Refresh materialized view every 10 transaction updates
      if (refreshCount.current >= 10) {
        analyticsAggregationService.refreshDashboardSummary();
        queryClient.invalidateQueries({ queryKey: ['dashboard-summary-view'] });
        refreshCount.current = 0;
      }
    }
  }, [latestUpdate, queryClient]);

  return {
    ...query,
    refresh: useCallback(async () => {
      await analyticsAggregationService.refreshDashboardSummary();
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary-view'] });
    }, [queryClient]),
  };
}

/**
 * Hook for combined analytics data (for comprehensive dashboards)
 */
export function useCombinedAnalytics(mandateId?: string) {
  const dashboardSummary = useDashboardSummary();
  const transactionAnalytics = useTransactionAnalytics({ mandateId });
  const processingAnalytics = useProcessingAnalytics();
  const teamAnalytics = useTeamAnalytics();

  const isLoading = dashboardSummary.isLoading ||
                   transactionAnalytics.isLoading ||
                   processingAnalytics.isLoading ||
                   teamAnalytics.isLoading;

  const isError = dashboardSummary.isError ||
                  transactionAnalytics.isError ||
                  processingAnalytics.isError ||
                  teamAnalytics.isError;

  const refresh = useCallback(() => {
    dashboardSummary.refresh();
    transactionAnalytics.refresh();
    processingAnalytics.refresh();
    teamAnalytics.refresh();
  }, [dashboardSummary, transactionAnalytics, processingAnalytics, teamAnalytics]);

  return {
    dashboardSummary: dashboardSummary.data,
    transactionAnalytics: transactionAnalytics.data,
    processingAnalytics: processingAnalytics.data,
    teamAnalytics: teamAnalytics.data,
    isLoading,
    isError,
    refresh,
  };
}

/**
 * Hook for real-time KPI monitoring
 */
export function useRealtimeKPIs() {
  const [kpis, setKpis] = useState({
    transactionsToday: 0,
    automationRate: 0,
    averageConfidence: 0,
    pendingReviews: 0,
    processingQueues: 0,
    teamProductivity: 0,
    lastUpdated: new Date(),
  });

  const { latestUpdate } = useRealtimeContext();
  const dashboardSummary = useDashboardSummary({ refreshInterval: 30000 });
  const transactionAnalytics = useTransactionAnalytics({ refreshInterval: 30000 });

  // Update KPIs when data changes
  useEffect(() => {
    const summary = dashboardSummary.data;
    const analytics = transactionAnalytics.data;

    if (summary && analytics && analytics.length > 0) {
      const todayAnalytics = analytics[0];

      setKpis({
        transactionsToday: summary.totalTransactionsToday ?? 0,
        automationRate: summary.avgAutomationRate ?? 0,
        averageConfidence: todayAnalytics.averageConfidenceScore ?? 0,
        pendingReviews: summary.pendingTransactions ?? 0,
        processingQueues: summary.flaggedTransactions ?? 0,
        teamProductivity: todayAnalytics.automationRate ?? 0,
        lastUpdated: new Date(),
      });
    }
  }, [dashboardSummary.data, transactionAnalytics.data]);

  // Real-time updates
  useEffect(() => {
    if (latestUpdate) {
      setKpis(prev => ({
        ...prev,
        lastUpdated: new Date(latestUpdate.timestamp),
      }));
    }
  }, [latestUpdate]);

  return kpis;
}

/**
 * Hook for analytics cache management
 */
export function useAnalyticsCache() {
  const clearCache = useCallback(() => {
    analyticsAggregationService.clearCache();
  }, []);

  const getCacheStats = useCallback(() => {
    return analyticsAggregationService.getCacheStats();
  }, []);

  const populateHistoricalAnalytics = useCallback(async () => {
    await analyticsAggregationService.populateHistoricalAnalytics();
  }, []);

  return {
    clearCache,
    getCacheStats,
    populateHistoricalAnalytics,
  };
}